import React, { useState } from "react";
import { useLazyQuery, gql } from "@apollo/client";
import Loader from "../../Loader";

const GET_ANALYTICS_USER_TIMELINE = gql`
  query getAnalyticsUserTimeline($userId: String, $deviceId: String, $limit: Int) {
    getAnalyticsUserTimeline(userId: $userId, deviceId: $deviceId, limit: $limit) {
      status
      message
      data {
        eventName
        screenName
        propertiesJson
        platform
        appVersion
        createdAt
      }
    }
  }
`;

// Same query CreateBooking.jsx already uses to resolve a phone number to a
// user record — reused here rather than adding a new backend lookup.
const GET_USER_BY_PHONE = gql`
  query getUserlist($contactNumber: String!) {
    getUserlist(data: { contactNumber: $contactNumber }) {
      status
      message
      user {
        id
        fullName
        contactNumber
      }
    }
  }
`;

const IDENTIFIER_TYPES = [
  { value: "phone", label: "Phone Number" },
  { value: "userId", label: "User ID" },
  { value: "deviceId", label: "Device ID" },
];

const formatProperties = (propertiesJson) => {
  try {
    const parsed = JSON.parse(propertiesJson || "{}");
    const entries = Object.entries(parsed);
    if (entries.length === 0) return "—";
    return entries.map(([key, value]) => `${key}: ${JSON.stringify(value)}`).join(", ");
  } catch (e) {
    return propertiesJson || "—";
  }
};

// Per-user event timeline for support/debugging — "what did this user actually
// do before hitting the issue they reported". See ANALYTICS_PLATFORM_PLAN.md §12.
const UserTimelineWidget = () => {
  const [identifierType, setIdentifierType] = useState(IDENTIFIER_TYPES[0].value);
  const [identifierValue, setIdentifierValue] = useState("");
  const [resolvedUser, setResolvedUser] = useState(null);
  const [lookupError, setLookupError] = useState(null);

  const [fetchTimeline, { data, loading, error, called }] = useLazyQuery(
    GET_ANALYTICS_USER_TIMELINE,
    { fetchPolicy: "network-only" },
  );

  const [lookupUserByPhone, { loading: phoneLookupLoading }] = useLazyQuery(
    GET_USER_BY_PHONE,
    { fetchPolicy: "network-only" },
  );

  const handleIdentifierTypeChange = (value) => {
    setIdentifierType(value);
    setResolvedUser(null);
    setLookupError(null);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const value = identifierValue.trim();
    if (!value) return;

    setResolvedUser(null);
    setLookupError(null);

    if (identifierType === "phone") {
      if (value.length !== 10) {
        setLookupError("Enter a 10-digit phone number");
        return;
      }
      const { data: phoneData } = await lookupUserByPhone({
        variables: { contactNumber: value },
      });
      const matchedUser = phoneData?.getUserlist?.user?.[0];
      if (!matchedUser) {
        setLookupError("No user found with this phone number");
        return;
      }
      setResolvedUser(matchedUser);
      fetchTimeline({ variables: { userId: matchedUser.id, deviceId: null, limit: 200 } });
      return;
    }

    fetchTimeline({
      variables: {
        userId: identifierType === "userId" ? value : null,
        deviceId: identifierType === "deviceId" ? value : null,
        limit: 200,
      },
    });
  };

  const rows = data?.getAnalyticsUserTimeline?.data || [];
  const identifierPlaceholder =
    identifierType === "phone"
      ? "10-digit phone number"
      : `Enter ${identifierType === "userId" ? "user" : "device"} ID`;

  return (
    <div className="bg-[#1C1C1E] p-6 rounded-2xl shadow-lg border border-gray-800">
      <h3 className="text-[#FBB033] font-bold text-xl mb-4">User Event Timeline</h3>
      <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-3 mb-6">
        <select
          className="bg-gray-800 text-gray-200 text-sm rounded-md px-2 py-2 border border-gray-700"
          value={identifierType}
          onChange={(e) => handleIdentifierTypeChange(e.target.value)}
        >
          {IDENTIFIER_TYPES.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type={identifierType === "phone" ? "tel" : "text"}
          className="bg-gray-800 text-gray-200 text-sm rounded-md px-3 py-2 border border-gray-700 flex-1 min-w-[240px]"
          placeholder={identifierPlaceholder}
          value={identifierValue}
          onChange={(e) => setIdentifierValue(e.target.value)}
        />
        <button
          type="submit"
          disabled={phoneLookupLoading}
          className="bg-[#FBB033] text-black font-semibold text-sm px-4 py-2 rounded-md hover:opacity-90 disabled:opacity-50"
        >
          {phoneLookupLoading ? "Looking up..." : "Search"}
        </button>
      </form>

      {lookupError && <div className="text-red-500 mb-4">{lookupError}</div>}

      {resolvedUser && (
        <div className="text-sm text-gray-400 mb-4">
          Showing timeline for <span className="text-gray-200 font-medium">{resolvedUser.fullName}</span>{" "}
          ({resolvedUser.contactNumber}) — user ID: {resolvedUser.id}
        </div>
      )}

      {loading && <Loader />}
      {error && <div className="text-red-500">Error loading user timeline</div>}

      {!loading && called && rows.length === 0 && (
        <div className="text-gray-500">No events found for this identifier.</div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-300">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="py-2 pr-4">Time</th>
                <th className="py-2 pr-4">Event</th>
                <th className="py-2 pr-4">Screen</th>
                <th className="py-2 pr-4">Properties</th>
                <th className="py-2">Platform</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index} className="border-b border-gray-800 align-top">
                  <td className="py-2 pr-4 whitespace-nowrap font-mono text-xs">
                    {new Date(row.createdAt).toLocaleString()}
                  </td>
                  <td className="py-2 pr-4 font-medium">{row.eventName}</td>
                  <td className="py-2 pr-4">{row.screenName || "—"}</td>
                  <td className="py-2 pr-4 break-all text-xs text-gray-400">
                    {formatProperties(row.propertiesJson)}
                  </td>
                  <td className="py-2 text-xs text-gray-500">
                    {row.platform} {row.appVersion}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserTimelineWidget;
